import { IntegrationPropertyDetailed } from '@/entities/integrationProperty';
import { integrationsConfigService } from '@/services/integrations/integrationsConfigService';
import { integrationsService } from '@/services/integrations/integrationsService';
import PrismaticClient from '@/services/integrations/prismaticClient';
import {
  ActivatedIntegration,
  ConfigVariable,
  ConfigVariableKey,
  InputInstanceConfigVariable,
  Instance,
  IntegrationDetailed,
  IntegrationID,
} from '@/services/integrations/types';
import { defineStore } from 'pinia';

type ConnectionInputs = Array<ConnectionConfigVariableWithType>;
type Config = Record<string, string | ConnectionInputs>;

interface IntegrationDetailsState {
  id: IntegrationID | null;
  integration: IntegrationDetailed | null;
  instance: Instance | null;
  loadingInstance: boolean;
  properties: IntegrationPropertyDetailed;
  multiStep: {
    currentStep: number;
    config: Config;
  };
  isModalOpen: boolean;
}

const defaultState = (): IntegrationDetailsState => ({
  id: null,
  integration: null,
  instance: null,
  loadingInstance: false,
  properties: {
    category: null,
    name: '',
    help_center_url: null,
    prismatic_id: '',
    about: null,
    activation_instruction: null,
    overview: null,
    enabled: false,
  },
  multiStep: {
    currentStep: 0,
    config: {},
  },
  isModalOpen: false,
});

export const useIntegrationDetailsStore = defineStore('integrationDetails', {
  state: defaultState,

  getters: {
    isActivated(): boolean {
      return this.integration ? integrationsService.isActivated(this.integration) : false;
    },

    isEnabled(): boolean {
      return this.isActivated && integrationsService.isEnabled(this.integration as ActivatedIntegration);
    },

    isSingleStep(): boolean {
      return this.integration ? integrationsConfigService.isSingleStepConfiguration(this.integration) : false;
    },
  },

  actions: {
    async init(integrationId: IntegrationID) {
      if (this.id !== integrationId) {
        this.resetState();
        this.id = integrationId;
      }

      const integration = await this.loadIntegration();
      await this.loadProperties(integration);
      this.integration = this.fillUpProperties(integration, this.properties);
    },

    resetState() {
      Object.assign(this, defaultState());
    },

    async loadIntegration() {
      if (this.id === null) {
        throw new Error('Integration ID is not set');
      }

      if (this.integration && this.integration.id === this.id) {
        return this.integration;
      }

      return PrismaticClient.getIntegration(this.id);
    },

    loadProperties(integration: IntegrationDetailed): Promise<void> {
      if (integration === null) {
        throw new Error('Integration is not loaded');
      }

      const integrationId = integration.firstVersion.id;

      if (this.properties && this.properties.prismatic_id === integrationId) {
        return Promise.resolve();
      }

      return integrationsService.getProperties(integrationId)
        .then(p => { this.properties = p; })
        .catch(() => {
          this.properties = {
            category: null,
            name: '',
            help_center_url: null,
            prismatic_id: this.id as string,
            about: null,
            activation_instruction: null,
            overview: null,
            enabled: false,
          };
        });
    },

    fillUpProperties(integration: IntegrationDetailed, properties: IntegrationPropertyDetailed) {
      integration.name = properties.name || integration.name;
      integration.description = properties.overview || integration.description;
      integration.overview = properties.about || integration.overview;
      integration.enabled = properties.enabled;

      return integration;
    },

    getAndCheckIntegrationActivated(): ActivatedIntegration {
      if (!this.isActivated) {
        throw new Error('Integration is not activated or is not loaded');
      }

      return this.integration as ActivatedIntegration;
    },

    setConfigVariable(key: string, value: string) {
      this.multiStep.config[key] = value;
    },

    getConfigVariable(key: string) {
      return this.multiStep.config[key];
    },

    /**
     * @param key Connection config variable key
     * @param name Name of the input variable amongst connection inputs
     * @param value
     */
    setConnectionConfigVariable(key: ConfigVariableKey, name: string, value: string) {
      this.multiStep.config[key] = this.multiStep.config[key] || [];
      const connectionVariable = (this.multiStep.config[key] as ConnectionInputs).find(c => c.name === name);

      if (connectionVariable) {
        connectionVariable.value = value;
        return;
      }

      (this.multiStep.config[key] as ConnectionInputs).push({
        name,
        type: 'value',
        value,
      });
    },

    getConnectionConfigVariable(key: ConfigVariableKey, name: string): string|undefined {
      const inputs = (this.multiStep.config[key] || []) as ConnectionInputs;
      return inputs.find(c => c.name === name)?.value;
    },

    nextStep() {
      ++this.multiStep.currentStep;
    },

    setStep(step: number) {
      this.multiStep.currentStep = step;
    },

    openModal() {
      this.isModalOpen = true;
    },

    async openMultiStepModal(reconfigure = false) {
      if (!reconfigure) {
        this.instance = await integrationsConfigService.createInstance(this.integration!);
      } else {
        this.instance = this.getAndCheckIntegrationActivated().instances.nodes[0];
      }

      this.resetMultiStepConfig();
      this.readConfigFromInstance(this.instance);
      this.openModal();
    },

    resetMultiStepConfig() {
      this.multiStep.currentStep = 0;
      this.multiStep.config = {};
    },

    readConfigFromInstance(instance: Instance) {
      instance.configVariables.nodes.forEach((configVariable: ConfigVariable) => {
        if (configVariable.inputs?.nodes?.length) {
          this.multiStep.config[configVariable.requiredConfigVariable.key] = configVariable.inputs.nodes.map(input => {
            return {
              ...input,
              type: 'value',
            };
          });
        } else if (configVariable.value) {
          this.multiStep.config[configVariable.requiredConfigVariable.key] = configVariable.value;
        }
      });
    },

    closeModal() {
      this.isModalOpen = false;
    },

    async saveAndDeploy() {
      const inputConfigVariables = this.formatConfigVariablesForDeploy(this.multiStep.config);
      await PrismaticClient.updateInstanceConfigVariables(this.instance!.id, inputConfigVariables);

      return integrationsConfigService.deployInstance(this.instance!, this.integration!);
    },

    formatConfigVariablesForDeploy(config: Config): InputInstanceConfigVariable[] {
      return Object.entries(config).map(([key, value]): InputInstanceConfigVariable => {
        return typeof value === 'object' ? {
          key,
          values: JSON.stringify(value),
        } : {
          key,
          value,
        };
      });
    },
  },
});
